import * as Tooltip from "@radix-ui/react-tooltip";
import { DeviceDeleteTooltip } from "../../style/contents/backup/backup";
import { downloadS3File } from "../../util/download.s3";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { DbBackupFileItem } from "../../view/contents/db/db.file.list";
import Pagination from "../common/table.pagination";
import { fetchRequestWormFile } from "../../api/admin";
import ModalCenterLayout from "../modal/modal.center.layout";
import { NewTableContainer } from "../../style/contents/db/db";

interface DbFileListTableProps {
  data: DbBackupFileItem[];
  searchKeyword?: string;
  pageSize: number;
  paginationEnabled?: boolean;
  isLoading?: boolean;
  userId: string;
  loginToken: string;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const formatFileSize = (size: number) => {
  if (!size) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (value: string | number) => {
  if (!value) return "-";

  const timestamp =
    typeof value === "number" || /^\d+$/.test(String(value))
      ? Number(value)
      : value;
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return String(value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toSearchText = (file: DbBackupFileItem) =>
  [
    file.fileName,
    file.type,
    file.size,
    formatFileSize(Number(file.size)),
    formatDate(file.createdDate),
  ]
    .map((value) => String(value ?? "").toLowerCase())
    .join(" ");

const DbFileListTable = ({
  data,
  searchKeyword = "",
  pageSize,
  paginationEnabled = true,
  isLoading,
  userId,
  loginToken,
}: DbFileListTableProps) => {
  console.log(data);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [wormTargetFile, setWormTargetFile] = useState<DbBackupFileItem | null>(
    null,
  );
  const [wormModal, setWormModal] = useState({
    isOpen: false,
    isVisible: false,
  });

  const handleOpenWormModal = useCallback((file: DbBackupFileItem) => {
    console.log("[DbFileList WORM] selected row", file);
    setWormTargetFile(file);
    setWormModal({ isOpen: true, isVisible: true });
  }, []);

  const handleCloseWormModal = useCallback(() => {
    setWormTargetFile(null);
    setWormModal({ isOpen: false, isVisible: false });
  }, []);

  const handleRequestWormFile = useCallback(async () => {
    if (!userId || !loginToken) {
      alert("사용자 또는 로그인 토큰 정보가 없습니다.");
      return;
    }

    if (
      wormTargetFile?.fileId == null ||
      wormTargetFile.backupHistoryId == null
    ) {
      console.log("[DbFileList WORM] missing file params", {
        userId,
        hasLoginToken: Boolean(loginToken),
        fileId: wormTargetFile?.fileId,
        backupHistoryId: wormTargetFile?.backupHistoryId,
        row: wormTargetFile,
      });
      alert("WORM 요청할 파일 정보가 없습니다.");
      return;
    }

    const params = {
      fileId: wormTargetFile.fileId,
      backupHistoryId: wormTargetFile.backupHistoryId,
    };

    try {
      console.log("[DbFileList WORM] file request params", {
        userId,
        hasLoginToken: Boolean(loginToken),
        ...params,
      });

      await fetchRequestWormFile(userId, params, loginToken);

      alert("WORM 요청이 접수되었습니다.");
      handleCloseWormModal();
    } catch (err) {
      console.error("WORM 파일 요청 실패:", err);
      alert("WORM 요청 중 오류가 발생했습니다.");
    }
  }, [handleCloseWormModal, loginToken, userId, wormTargetFile]);

  const columns = useMemo<ColumnDef<DbBackupFileItem>[]>(
    () => [
      {
        header: "종류",
        accessorKey: "type",
        cell: (info) => {
          const type = info.getValue();
          if (type === "db") return "DB";
          if (type === "image") return "이미지";
          return type ? String(type) : "-";
        },
        size: 120,
      },
      {
        header: "이름",
        accessorKey: "fileName",
        cell: ({ row, getValue }) => {
          const fileName = String(getValue() || "-");

          return (
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
            <span
              tabIndex={0}
              className="clickable-folder"
              onClick={() => downloadS3File(row.original.s3Url, fileName)}
            >
              {fileName}
            </span>
              </Tooltip.Trigger>
              <Tooltip.Portal>
                <DeviceDeleteTooltip side="top" sideOffset={6} collisionPadding={8}>
                  다운로드
                  <Tooltip.Arrow fill="#333333" />
                </DeviceDeleteTooltip>
              </Tooltip.Portal>
            </Tooltip.Root>
          );
        },
        size: 400,
      },
      {
        header: "크기",
        accessorKey: "size",
        cell: (info) => formatFileSize(Number(info.getValue())),
        size: 140,
      },
      {
        header: "백업날짜",
        accessorKey: "createdDate",
        cell: (info) => formatDate(info.getValue() as string | number),
        size: 220,
      },
      {
        header: "WORM",
        id: "worm",
        cell: ({ row }) => {
          const item = row.original;

          if (item.isWorm) return "적용됨";

          return (
            <button
              type="button"
              className="worm-request-btn"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenWormModal(item);
              }}
            >
              WORM
            </button>
          );
        },
        size: 120,
      },
    ],
    [handleOpenWormModal],
  );
  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return data;

    return data.filter((file) => toSearchText(file).includes(keyword));
  }, [data, searchKeyword]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: paginationEnabled
      ? getPaginationRowModel()
      : undefined,
    onPaginationChange: setPagination,
  });

  const totalRowCount = table.getRowCount();

  useEffect(() => {
    table.setPageSize(pageSize);
  }, [table, pageSize, filteredData]);

  useEffect(() => {
    table.setPageIndex(0);
  }, [table, searchKeyword, filteredData]);

  return (
    <Tooltip.Provider delayDuration={200}>
    <NewTableContainer>
      <div className="table-head-wrap">
        <div className="left">
          <span className="table-total-row">전체 {totalRowCount}</span>
        </div>
      </div>
      {isLoading ? (
        <div className="loading">데이터를 불러오는 중입니다...</div>
      ) : (
        <div className="fixed-height" style={{ maxHeight: "36rem" }}>
          <table>
            <thead>
              {table.getHeaderGroups().map((item) => (
                <tr key={item.id}>
                  {item.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="no-data-row">
                    검색 결과가 없습니다.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {paginationEnabled && table.getPageCount() > 0 && (
        <Pagination
          page={pagination.pageIndex}
          pages={table.getPageCount()}
          onPageChange={(page) => {
            table.setPageIndex(page);
          }}
        />
      )}
      {wormModal.isOpen && (
        <ModalCenterLayout
          isVisible={wormModal.isVisible}
          closedHandler={handleCloseWormModal}
          size="s"
        >
          <div className="worm-confirm-modal">
            <div className="modal-header">
              <h3>WORM 기능 적용</h3>
              <button
                type="button"
                className="closed-modal-btn"
                onClick={handleCloseWormModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>
                적용하시겠습니까? <br /> worm 기능이 적용되면 이미 저장된
                데이터의 수정/삭제가 불가능하게 됩니다.
              </p>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="cancel"
                onClick={handleCloseWormModal}
              >
                취소
              </button>
              <button
                type="button"
                className="confirm"
                onClick={handleRequestWormFile}
              >
                적용
              </button>
            </div>
          </div>
        </ModalCenterLayout>
      )}
    </NewTableContainer>
    </Tooltip.Provider>
  );
};

export default DbFileListTable;
